import React, { useRef, useState } from "react";
import { FaFileExcel, FaTimes, FaUpload } from "react-icons/fa";

import "./styles.css";

interface FileUploadProps {
  accept?: string;
  onFileSelect: (file: File | null) => void;
  placeholder?: string;
  maxSize?: number; 
  disabled?: boolean;
  multiple?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  accept = ".xlsx",
  onFileSelect,
  placeholder = "Selecione um arquivo",
  maxSize = 10,
  disabled = false,
  multiple = false, 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (file: File) => {
    if (file.size > maxSize * 1024 * 1024) {
      alert(`Arquivo muito grande. Tamanho máximo permitido: ${maxSize}MB`);
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const fileToProcess = multiple ? files[0] : files[0];
      handleFileChange(fileToProcess);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const fileToProcess = multiple ? files[0] : files[0];
      handleFileChange(fileToProcess);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="file-upload-container">
      <div
        className={`file-upload-area ${dragActive ? "drag-active" : ""} ${disabled ? "disabled" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          disabled={disabled}
          multiple={multiple}
          style={{ display: "none" }}
        />
        
        {selectedFile ? (
          <div className="file-selected">
            <div className="file-info">
              <FaFileExcel className="file-icon" />
              <div className="file-details">
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">{formatFileSize(selectedFile.size)}</span>
              </div>
            </div>
            <button
              type="button"
              className="remove-file-btn"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              disabled={disabled}
            >
              <FaTimes />
            </button>
          </div>
        ) : (
          <div className="file-upload-placeholder">
            <FaUpload className="upload-icon" />
            <p className="upload-text">{placeholder}</p>
            <p className="upload-hint">
              Arraste e solte ou clique para selecionar
            </p>
            <p className="file-format">Formatos aceitos: {accept}</p>
            <p className="file-size-limit">Tamanho máximo: {maxSize}MB</p>
            {!multiple && (
              <p className="single-file-note">Apenas 1 arquivo permitido</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
